// R17 Task B: per-user Manual Checks screen section (Peter #3/#6 gap —
// currently manual checks are visible only on PDF/Excel exports).
import React, { useMemo } from 'react';
import type { UserReportManualCheck, UserReportUser } from '../../types/report';
import { formatCurrency } from '../../utils/currencyUtils';
import { bucketChecksByTier } from '../../utils/manualCheckTiers';

interface UserReportManualChecksSectionProps {
  user: UserReportUser;
}

interface TierColumnProps {
  label: string;
  checks: UserReportManualCheck[];
}

const TierColumn: React.FC<TierColumnProps> = ({ label, checks }) => {
  const totalAmount = checks.reduce((s, c) => s + Number(c.amount || 0), 0);
  const totalYour = checks.reduce((s, c) => s + Number(c.your_commission || 0), 0);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
      <div className="bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700">
        {label} {checks.length > 0 && <span className="text-xs text-gray-500">({checks.length})</span>}
      </div>
      {checks.length === 0 ? (
        <div className="px-3 py-4 text-xs text-gray-400 italic">No checks in this tier</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-xs">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-500">Date</th>
                <th className="px-3 py-2 text-left font-medium text-gray-500">Firm</th>
                <th className="px-3 py-2 text-right font-medium text-gray-500">Amount</th>
                <th className="px-3 py-2 text-right font-medium text-gray-500">Payout %</th>
                <th className="px-3 py-2 text-right font-medium text-gray-500">Your Comm.</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {checks.map((c) => (
                <tr key={c.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 text-gray-900 whitespace-nowrap">{c.check_date}</td>
                  <td className="px-3 py-2 text-gray-900">{c.firm_name}</td>
                  <td className="px-3 py-2 text-right text-gray-900">{formatCurrency(Number(c.amount || 0))}</td>
                  <td className="px-3 py-2 text-right text-gray-700">{Number(c.payout_percentage || 0).toFixed(2)}%</td>
                  <td className="px-3 py-2 text-right font-medium text-blue-700">
                    {formatCurrency(Number(c.your_commission || 0))}
                  </td>
                </tr>
              ))}
              <tr className="bg-gray-50 font-semibold">
                <td colSpan={2} className="px-3 py-2 text-right text-gray-700">Subtotal:</td>
                <td className="px-3 py-2 text-right text-gray-900">{formatCurrency(totalAmount)}</td>
                <td></td>
                <td className="px-3 py-2 text-right text-blue-700">{formatCurrency(totalYour)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/**
 * Per-user Manual Checks screen section — mirrors the PDF/Excel layout
 * Peter has seen since R16 (20% / 10% / Misc tier bucketing) so screen
 * and PDF read identically. Empty check list → return null (no clutter
 * for salesmen with no checks in range).
 */
const UserReportManualChecksSection: React.FC<UserReportManualChecksSectionProps> = ({ user }) => {
  const checks = user.manual_checks ?? [];

  const { buckets, totalCount, totalYourCommission } = useMemo(() => {
    const b = bucketChecksByTier(checks);
    return {
      buckets: b,
      totalCount: checks.length,
      totalYourCommission: checks.reduce((s, c) => s + Number(c.your_commission || 0), 0),
    };
  }, [checks]);

  if (totalCount === 0) {
    return null;
  }

  return (
    <div className="mt-6 border-t border-gray-200 pt-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-gray-800">
          Manual Checks — {user.user_name}
          <span className="ml-2 text-xs font-normal text-gray-500">({totalCount} check{totalCount === 1 ? '' : 's'})</span>
        </h4>
        <div className="text-sm">
          <span className="text-xs text-gray-500">Total: </span>
          <span className="font-semibold text-blue-700">{formatCurrency(totalYourCommission)}</span>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <TierColumn label="20% Payout" checks={buckets.tier20} />
        <TierColumn label="10% Payout" checks={buckets.tier10} />
        <TierColumn label="Misc" checks={buckets.misc} />
      </div>
    </div>
  );
};

export default UserReportManualChecksSection;
